/**
 * `/crm/stages` — the pipeline's columns: their order, their win probability,
 * and after how many days a deal in them rots. Behind `crm.admin`.
 *
 * A stage that holds any deal — open, won or lost — cannot be deleted; the
 * server says how many, and the dialog shows it.
 */
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import Button from '@mui/material/Button';
import { CRM_LIMITS, CRM_PERMISSIONS, type StageDto } from '@forsys/shared';
import { useAuth } from '../../auth/useAuth';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { ErrorState } from '../../components/ErrorState';
import { FormDialog } from '../../components/FormDialog';
import { PageHeader } from '../../components/PageHeader';
import { useAsync } from '../../hooks/useAsync';
import { useCrudDialogs } from '../../hooks/useCrudDialogs';
import { useSubmit } from '../../hooks/useSubmit';
import { useStageActions } from './actions';
import { createStage, listStages } from './api';
import { BLANK_STAGE, createStageRequest, StageFields, stageIncomplete, type StageForm } from './forms';

export default function StagesPage() {
  const { t } = useTranslation('crm');
  const { can } = useAuth();
  const stages = useAsync(listStages, []);
  const rows = stages.data?.stages ?? [];
  const actions = useStageActions(rows, { onChanged: () => stages.reload() });

  const crud = useCrudDialogs<StageDto, StageForm>({ blank: BLANK_STAGE, formFor: () => BLANK_STAGE });
  const create = useSubmit(async () => {
    const result = await createStage(createStageRequest(crud.form));
    stages.reload();
    return result;
  });

  return (
    <>
      <PageHeader
        title={t('stage.title')}
        description={t('stage.description')}
        actions={
          can(CRM_PERMISSIONS.ADMIN) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={rows.length >= CRM_LIMITS.stages}
              onClick={() => crud.openCreate()}
            >
              {t('stage.new')}
            </Button>
          )
        }
      />

      {actions.error && <ErrorState error={actions.error} />}

      <AsyncContent state={stages}>
        {({ stages: list }) => (
          <EntityTable<StageDto>
            rows={list}
            rowKey={(stage) => stage.id}
            empty={{
              icon: <ViewKanbanOutlinedIcon fontSize="inherit" />,
              title: t('stage.emptyTitle'),
              description: t('stage.emptyDescription'),
            }}
            columns={[
              { field: 'position', label: '#', width: 48, render: (stage) => stage.position + 1 },
              { field: 'name', label: t('field.name'), render: (stage) => stage.name },
              {
                field: 'probability',
                label: t('field.probability'),
                align: 'right',
                width: 140,
                render: (stage) => `${stage.probability} %`,
              },
              {
                field: 'rottenDays',
                label: t('field.rottenDays'),
                align: 'right',
                width: 160,
                render: (stage) => (stage.rottenDays === null ? t('stage.never') : t('stage.days', { count: stage.rottenDays })),
              },
            ]}
            actions={actions.rowActions}
            actionsWidth={180}
          />
        )}
      </AsyncContent>
      {actions.dialogs}

      <FormDialog
        open={crud.isOpen}
        title={t('stage.createDialog.title')}
        submitLabel={t('stage.createDialog.submit')}
        maxWidth="xs"
        pending={create.pending}
        error={create.error}
        disabled={stageIncomplete(crud.form)}
        onClose={crud.close}
        onSubmit={async () => {
          if (await create.submit()) crud.close();
        }}
      >
        <StageFields form={crud.form} patch={crud.patch} />
      </FormDialog>
    </>
  );
}
